import React from "react";
import { motion } from "framer-motion";
import { Song } from "../../types/song";
import { useTheme } from "../../context/ThemeContext";
import { getAvailablePlatforms } from "../../config/platforms";
import { PlatformIcon, InstagramIcon } from "./PlatformIcons";

interface ListenEverywhereProps {
  song: Song;
}

const ListenEverywhere: React.FC<ListenEverywhereProps> = ({ song }) => {
  const { currentTheme } = useTheme();
  const platforms = getAvailablePlatforms(song);
  const reelUrl = song.instagramReelUrl?.trim();
  if (platforms.length === 0 && !reelUrl) return null;

  return (
    <section className={`bg-gradient-to-br ${currentTheme.colors.primary} text-white py-12 px-6`}>
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">Listen Everywhere</h2>
          <div className="w-24 h-1 bg-purple-300 mx-auto" />
        </motion.div>

        {platforms.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {platforms.map(({ platform, url }, index) => (
              <motion.a
                key={platform.id}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                whileHover={{ scale: 1.03, y: -3 }}
                whileTap={{ scale: 0.97 }}
                className="min-h-[56px] flex items-center gap-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-6 py-4 hover:bg-white/20 transition-colors"
              >
                <div className={`w-10 h-10 shrink-0 rounded-full bg-gradient-to-br ${platform.brand} flex items-center justify-center text-white`}>
                  <PlatformIcon id={platform.id} className="w-5 h-5" />
                </div>
                <span className="font-semibold text-lg">{platform.name}</span>
              </motion.a>
            ))}
          </div>
        )}

        {reelUrl && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-center"
          >
            {platforms.length > 0 && <div className="w-full h-px bg-white/20 my-10" />}
            <p className="text-purple-100 mb-5">Use this song's audio on Instagram and share your own Reel.</p>
            <motion.a
              href={reelUrl}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 min-h-[56px] px-8 rounded-full bg-gradient-to-r from-[#833AB4] via-[#E1306C] to-[#FD1D1D] text-white font-bold text-lg shadow-xl"
            >
              <InstagramIcon className="w-5 h-5" />
              Create a Reel on Instagram
            </motion.a>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default ListenEverywhere;
